import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Marker } from "google-maps-react";

import { expandClusterForZoom, getWorkerRoleBucket } from "./heatmapHelpers";
import {
  buildClusterIconUrl,
  buildMarkerIconUrl,
  CLUSTER_ICON_SIZE,
  MARKER_ICON_SIZE,
} from "./markerIcon";

/** Identifies a pin by who is on it rather than by its index in the current
 * clustering, so a pin that survives a zoom change keeps the same React key
 * (and therefore the same underlying google.maps.Marker) instead of being
 * unmounted and re-created. */
const markerKeyFor = (groupOrMember, fallback) => {
  const ids = groupOrMember.members.map((member) => member.id);
  return ids.every(Boolean) ? ids.join("-") : fallback;
};

const EventHeatMapMarkers = ({ google, map, clusters, zoom, avatarDataUrls, onMarkerClick }) => {
  /** `Marker` re-creates its google.maps.Marker whenever `position` or `icon`
   * change by reference, which flickers every pin on the map. Zoom changes
   * rebuild the whole cluster tree, so identical descriptors are reused from
   * the previous render rather than rebuilt, leaving unaffected pins alone. */
  const descriptorCache = useRef(new Map());

  const markers = useMemo(() => {
    const previous = descriptorCache.current;
    const next = new Map();

    clusters.forEach((cluster) => {
      const fallbackKey = `${cluster.lat}:${cluster.lng}`;

      expandClusterForZoom(cluster, zoom).forEach((groupOrMember, index) => {
        const isGroup = groupOrMember.members.length > 1;
        const primaryMember = groupOrMember.members[0];
        const markerKey = markerKeyFor(groupOrMember, `${fallbackKey}-${index}`);

        const url = isGroup
          ? buildClusterIconUrl(groupOrMember.members.length)
          : buildMarkerIconUrl({
              imageUrl: avatarDataUrls.get(primaryMember.user?.image),
              name: primaryMember.user?.name,
              bucket: getWorkerRoleBucket(primaryMember),
            });

        const cached = previous.get(markerKey);
        if (
          cached &&
          cached.icon.url === url &&
          cached.position.lat === groupOrMember.lat &&
          cached.position.lng === groupOrMember.lng
        ) {
          next.set(markerKey, { ...cached, groupOrMember });
          return;
        }

        const size = isGroup ? CLUSTER_ICON_SIZE : MARKER_ICON_SIZE;
        next.set(markerKey, {
          markerKey,
          groupOrMember,
          position: { lat: groupOrMember.lat, lng: groupOrMember.lng },
          icon: {
            url,
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2),
          },
        });
      });
    });

    descriptorCache.current = next;
    return [...next.values()];
  }, [google, clusters, zoom, avatarDataUrls]);

  return (
    <>
      {markers.map(({ markerKey, groupOrMember, position, icon }) => (
        <Marker
          key={markerKey}
          map={map}
          google={google}
          position={position}
          onClick={onMarkerClick(groupOrMember, markerKey)}
          icon={icon}
        />
      ))}
    </>
  );
};

EventHeatMapMarkers.propTypes = {
  google: PropTypes.object.isRequired,
  map: PropTypes.object,
  clusters: PropTypes.array.isRequired,
  zoom: PropTypes.number.isRequired,
  avatarDataUrls: PropTypes.instanceOf(Map).isRequired,
  onMarkerClick: PropTypes.func.isRequired,
};

EventHeatMapMarkers.defaultProps = {
  map: null,
};

export default EventHeatMapMarkers;
